// ItineraryManager.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";

// Use the maintained fork for drag and drop
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// Helper function to reorder an array
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const ItineraryManager = () => {
  const [days, setDays] = useState([]);
  const [newDay, setNewDay] = useState({ date: "", day: "", events: [] });
  const [newEvent, setNewEvent] = useState({
    title: "",
    time: "",
    description: "",
    address: "",
  });
  const [selectedDayId, setSelectedDayId] = useState("");
  // Track expand/collapse state for each day (by day id)
  const [expandedDays, setExpandedDays] = useState({});

  // Listen for itinerary updates from Firestore
  useEffect(() => {
    const q = query(collection(db, "itineraries"), orderBy("date", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const daysData = [];
      snapshot.forEach((doc) => {
        daysData.push({ id: doc.id, ...doc.data() });
      });
      setDays(daysData);
    });
    return () => unsubscribe();
  }, []);

  // Create a new day itinerary
  const addNewDay = async () => {
    try {
      await addDoc(collection(db, "itineraries"), newDay);
      setNewDay({ date: "", day: "", events: [] });
    } catch (error) {
      console.error("Error adding new day:", error);
    }
  };

  // Add a new event to a specific day
  const addEventToDay = async (dayId) => {
    const dayDoc = days.find((day) => day.id === dayId);
    if (dayDoc) {
      const updatedEvents = [...dayDoc.events, newEvent];
      try {
        await updateDoc(doc(db, "itineraries", dayId), { events: updatedEvents });
        setNewEvent({ title: "", time: "", description: "", address: "" });
      } catch (error) {
        console.error("Error adding event:", error);
      }
    }
  };

  // Update the order of events (after reordering)
  const updateEventOrder = async (dayId, updatedEvents) => {
    try {
      await updateDoc(doc(db, "itineraries", dayId), { events: updatedEvents });
    } catch (error) {
      console.error("Error updating event order:", error);
    }
  };

  // Delete an event from a specific day by index
  const deleteEvent = async (dayId, eventIndex) => {
    const dayDoc = days.find((day) => day.id === dayId);
    if (dayDoc) {
      const updatedEvents = dayDoc.events.filter((_, idx) => idx !== eventIndex);
      try {
        await updateDoc(doc(db, "itineraries", dayId), { events: updatedEvents });
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  // Edit an event by replacing it with updated data
  const editEvent = async (dayId, eventIndex, updatedEventData) => {
    const dayDoc = days.find((day) => day.id === dayId);
    if (dayDoc) {
      const updatedEvents = dayDoc.events.map((event, idx) =>
        idx === eventIndex ? updatedEventData : event
      );
      try {
        await updateDoc(doc(db, "itineraries", dayId), { events: updatedEvents });
      } catch (error) {
        console.error("Error editing event:", error);
      }
    }
  };

  // Handle drag and drop reordering for a specific day
  const handleDragEnd = (dayId) => (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const dayDoc = days.find((day) => day.id === dayId);
    if (!dayDoc) return;

    const reorderedEvents = reorder(dayDoc.events, sourceIndex, destinationIndex);

    // Update local state for immediate UI feedback
    setDays((prevDays) =>
      prevDays.map((d) => (d.id === dayId ? { ...d, events: reorderedEvents } : d))
    );

    // Persist the new order in Firestore
    updateEventOrder(dayId, reorderedEvents);
  };

  // Toggle expand/collapse for a specific day
  const toggleExpand = (dayId) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayId]: prev[dayId] === undefined ? false : !prev[dayId],
    }));
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Itinerary Manager</h1>

      {/* Section to add a new day */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold">Add New Day</h2>
        <input
          type="text"
          placeholder="Date (e.g., December 12, 2025)"
          value={newDay.date}
          onChange={(e) => setNewDay({ ...newDay, date: e.target.value })}
          className="border p-2 mr-2"
        />
        <input
          type="text"
          placeholder="Day (e.g., Friday)"
          value={newDay.day}
          onChange={(e) => setNewDay({ ...newDay, day: e.target.value })}
          className="border p-2 mr-2"
        />
        <button onClick={addNewDay} className="bg-blue-500 text-white px-4 py-2">
          Add Day
        </button>
      </div>

      {/* Section to add a new event */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold">Add New Event</h2>
        <select
          value={selectedDayId}
          onChange={(e) => setSelectedDayId(e.target.value)}
          className="border p-2 mr-2"
        >
          <option value="">Select Day</option>
          {days.map((day) => (
            <option key={day.id} value={day.id}>
              {day.date} - {day.day}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Activity Name"
          value={newEvent.title}
          onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
          className="border p-2 mr-2"
        />
        <input
          type="text"
          placeholder="Time (e.g., 8:00 AM)"
          value={newEvent.time}
          onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
          className="border p-2 mr-2"
        />
        <input
          type="text"
          placeholder="Short Description"
          value={newEvent.description}
          onChange={(e) =>
            setNewEvent({ ...newEvent, description: e.target.value })
          }
          className="border p-2 mr-2"
        />
        <input
          type="text"
          placeholder="Address"
          value={newEvent.address}
          onChange={(e) =>
            setNewEvent({ ...newEvent, address: e.target.value })
          }
          className="border p-2 mr-2"
        />
        <button
          onClick={() => addEventToDay(selectedDayId)}
          className="bg-green-500 text-white px-4 py-2"
          disabled={!selectedDayId}
        >
          Add Event
        </button>
      </div>

      {/* List of itinerary days and their events */}
      <div>
        <h2 className="text-xl font-semibold">Itinerary Days</h2>
        {days.map((day) => {
          // Default to expanded if not set
          const isExpanded = expandedDays[day.id] === undefined ? true : expandedDays[day.id];
          return (
            <div key={day.id} className="bg-white p-4 shadow-md rounded-lg mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">
                  {day.date} - {day.day}
                </h3>
                <button
                  onClick={() => toggleExpand(day.id)}
                  className="text-2xl focus:outline-none"
                  aria-label="Toggle Day"
                >
                  {isExpanded ? "▼" : "►"}
                </button>
              </div>
              {isExpanded && (
                <DragDropContext onDragEnd={handleDragEnd(day.id)}>
                  <Droppable droppableId={day.id}>
                    {(provided) => (
                      <ul
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="mt-2"
                      >
                        {day.events.map((event, idx) => (
                          <Draggable
                            key={`${day.id}-${idx}`}
                            draggableId={`${day.id}-${idx}`}
                            index={idx}
                          >
                            {(provided) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="border-l-4 border-indigo-500 pl-4 my-2 bg-gray-50"
                              >
                                <p className="text-sm text-gray-600">
                                  {event.time}
                                </p>
                                <h4 className="text-xl font-semibold">
                                  {event.title}
                                </h4>
                                <p className="text-gray-700">
                                  {event.description}
                                </p>
                                <p className="text-gray-700">
                                  Address: {event.address}
                                </p>
                                <button
                                  onClick={() => deleteEvent(day.id, idx)}
                                  className="bg-red-500 text-white px-2 py-1 mt-1 mr-2"
                                >
                                  Delete
                                </button>
                                <button
                                  onClick={() =>
                                    editEvent(day.id, idx, {
                                      title:
                                        prompt("New title:", event.title) ||
                                        event.title,
                                      time:
                                        prompt("New time:", event.time) ||
                                        event.time,
                                      description:
                                        prompt(
                                          "New description:",
                                          event.description
                                        ) || event.description,
                                      address:
                                        prompt("New address:", event.address) ||
                                        event.address,
                                    })
                                  }
                                  className="bg-yellow-500 text-white px-2 py-1 mt-1"
                                >
                                  Edit
                                </button>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ItineraryManager;
